/**
 * The customers list (wave 13, card 3d9c00e6): `GET /api/v1/analytics/customers`, its
 * `.csv`, the search and the audited reveal (`modules/customer_list`, whose README is
 * the contract). `analytics.read` to read and search by email, `exports.create` for the
 * CSV, `customers.reveal` (Owner, Administrator) to reveal a full website customer id or
 * search by one.
 *
 * Customers are masked as served: `customerRef` is the API's masked reference and the
 * only identifying value a row carries. The full id leaves the API only through the
 * reveal, one customer at a time, and is never kept beyond the screen that asked.
 *
 * Every figure the API cannot give arrives as `null` and stays `null` here: a column it
 * names in `not_available` is missing data with its reason, any other `null` is
 * undefined for that row (no orders, so no AOV) and reads "—". Never 0 in its place.
 * Money stays in integer minor units of `meta.currency`, as on the wire.
 */

import type { GlobalFilters } from '@/lib/filters'

import { csvFilename, DEFAULT_SORT_ORDER, SORT_ORDERS, type CsvFile, type SortOrder } from './list'
import { overviewSearch, type OverviewMetaOut } from './overview'
import type { Envelope } from './types'

/** `sort` values the API takes (README *Sort keys*); the default is `last_purchase_at`. */
export const CUSTOMER_SORTS = [
  'last_purchase_at',
  'first_purchase_at',
  'registered_at',
  'orders',
  'tickets',
  'range_orders',
  'range_tickets',
  'lifetime_revenue',
  'refunds',
  'aov',
  'range_revenue',
] as const
export type CustomerSort = (typeof CUSTOMER_SORTS)[number]

export const DEFAULT_CUSTOMER_SORT: CustomerSort = 'last_purchase_at'
export { DEFAULT_SORT_ORDER, SORT_ORDERS, type SortOrder }

/** Who is listed (README `basis`): the range's buyers (the default), its registrations, or everyone. */
export const CUSTOMER_BASES = ['purchased', 'registered', 'all'] as const
export type CustomerBasis = (typeof CUSTOMER_BASES)[number]
export const DEFAULT_CUSTOMER_BASIS: CustomerBasis = 'purchased'

/** New or returning in the range (the Overview KPIs' rule); null with no order in the range. */
export const CUSTOMER_TYPES = ['new', 'returning'] as const
export type CustomerType = (typeof CUSTOMER_TYPES)[number]

/** A consent category's state, and the marketing consent filter's values. */
export const CONSENT_STATES = ['granted', 'denied', 'withdrawn', 'unknown'] as const
export type ConsentState = (typeof CONSENT_STATES)[number]

/** Why a column has no value (README `not_available`). */
export const CUSTOMER_UNAVAILABLE_REASONS = ['customer_not_synced', 'no_segments_yet'] as const
export type CustomerUnavailableReason = (typeof CUSTOMER_UNAVAILABLE_REASONS)[number]

/** The first marketing touch the attribution job credited, as the visitor's link carried it. */
export interface CustomerFirstTouch {
  touchedAt: string
  source: string | null
  medium: string | null
  campaign: string | null
  /** The Suite campaign channel the link was built for, or null. */
  channelId: string | null
}

export interface CustomerConsent {
  /** The marketing (email) consent of record. */
  marketingState: ConsentState
  analytics: ConsentState
  advertising: ConsentState
  email: ConsentState
  push: ConsentState
  /** When the website last changed them; null when it never said. */
  updatedAt: string | null
}

export interface CustomerRow {
  // identity, masked
  /** The Suite's id of the synced record: what the reveal takes. Null until the record syncs. */
  customerId: string | null
  /** The website's customer id, masked (`••••12`). For display only: not unique, never an id. */
  customerRef: string
  websiteId: string
  environment: string
  synced: boolean
  // lifetime: every completed order, up to the request
  registeredAt: string | null
  firstPurchaseAt: string | null
  lastPurchaseAt: string | null
  orders: number
  tickets: number
  lifetimeRevenueMinor: number
  refundsMinor: number
  /** Null without an order. */
  aovMinor: number | null
  // the range's completed orders
  rangeOrders: number
  rangeTickets: number
  rangeRevenueMinor: number
  rangeRefundsMinor: number
  customerType: CustomerType | null
  // the synced record: null until it syncs
  firstTouch: CustomerFirstTouch | null
  isMember: boolean | null
  isVip: boolean | null
  consent: CustomerConsent | null
  /** Always null (`no_segments_yet`): no segment is stored anywhere yet. */
  segments: null
}

/** A row field that may be named in `notAvailable`. */
export type CustomerField = keyof CustomerRow

export interface CustomersList {
  /** This page, in `sort` / `order`. */
  rows: CustomerRow[]
  /** Every customer matching the filters, before paging. */
  total: number
  basis: CustomerBasis
  sort: CustomerSort
  order: SortOrder
  limit: number
  offset: number
  /** Each field that is null on a returned row for a reason, and the reason. */
  notAvailable: Partial<Record<CustomerField, CustomerUnavailableReason>>
  /** The currency every money column is in: `meta.currency`. */
  currency: string
  /** The tenant's timezone, which its days (and the period) are in. */
  timezone: string
}

export interface CustomersQuery {
  /** The global filters: the period, the website and the environments. */
  filters: GlobalFilters
  basis?: CustomerBasis
  customerType?: CustomerType
  /** True: members only; false: synced non-members only; absent: everyone. */
  member?: boolean
  vip?: boolean
  /** Marketing consent states to include; empty or absent: every state. */
  consent?: readonly ConsentState[]
  sort?: CustomerSort
  order?: SortOrder
  /** 1–200; the JSON routes only. */
  limit?: number
  /** The JSON routes only. */
  offset?: number
}

/**
 * What a search looks for: an email address (finds only customers who granted
 * advertising consent) or a full website customer id (`customers.reveal` only). It
 * travels in the request body, never in a URL.
 */
export type CustomerSearch = { email: string } | { externalCustomerId: string }

/** A revealed customer: the one place the full website customer id appears. */
export interface CustomerReveal {
  customerId: string
  websiteId: string
  environment: string
  externalCustomerId: string
}

/** The JSON routes' largest `limit`. */
export const MAX_CUSTOMERS_PAGE = 200


/** The most rows one CSV carries (README `GET /analytics/customers.csv`). */
export const CUSTOMERS_CSV_MAX_ROWS = 5000

export interface CustomersApi {
  customers(query: CustomersQuery): Promise<Envelope<CustomersList>>
  /** The list narrowed to the person `search` names (audited by the API); a 403 for an id search without `customers.reveal`. */
  searchCustomers(query: CustomersQuery, search: CustomerSearch): Promise<Envelope<CustomersList>>
  /** Every matching customer (up to `CUSTOMERS_CSV_MAX_ROWS`) as masked CSV (`exports.create`). */
  customersCsv(query: CustomersQuery): Promise<CsvFile>
  /** One synced customer's full website customer id (`customers.reveal`); audited by the API on every call. */
  revealCustomer(customerId: string): Promise<CustomerReveal>
}

// --- wire shapes (snake_case, as the API sends them) -------------------------

export interface CustomerFirstTouchOut {
  touched_at: string
  source: string | null
  medium: string | null
  campaign: string | null
  channel_id: string | null
}

export interface CustomerConsentOut {
  marketing_state: ConsentState
  analytics: ConsentState
  advertising: ConsentState
  email: ConsentState
  push: ConsentState
  updated_at: string | null
}

export interface CustomerRowOut {
  customer_id: string | null
  customer_ref: string
  website_id: string
  environment: string
  synced: boolean
  registered_at: string | null
  first_purchase_at: string | null
  last_purchase_at: string | null
  orders: number
  tickets: number
  lifetime_revenue_minor: number
  refunds_minor: number
  aov_minor: number | null
  range_orders: number
  range_tickets: number
  range_revenue_minor: number
  range_refunds_minor: number
  customer_type: CustomerType | null
  first_touch: CustomerFirstTouchOut | null
  is_member: boolean | null
  is_vip: boolean | null
  consent: CustomerConsentOut | null
  segments: null
}

export interface CustomersOut {
  data: {
    rows: CustomerRowOut[]
    total: number
    basis: CustomerBasis
    sort: CustomerSort
    order: SortOrder
    limit: number
    offset: number
    not_available: Record<string, string>
  }
  meta: OverviewMetaOut
}

export interface CustomerRevealOut {
  customer_id: string
  website_id: string
  environment: string
  external_customer_id: string
}

/** Wire column → row field: how `not_available`'s keys become the row's. */
const FIELDS = {
  customer_id: 'customerId',
  customer_ref: 'customerRef',
  website_id: 'websiteId',
  environment: 'environment',
  synced: 'synced',
  registered_at: 'registeredAt',
  first_purchase_at: 'firstPurchaseAt',
  last_purchase_at: 'lastPurchaseAt',
  orders: 'orders',
  tickets: 'tickets',
  lifetime_revenue_minor: 'lifetimeRevenueMinor',
  refunds_minor: 'refundsMinor',
  aov_minor: 'aovMinor',
  range_orders: 'rangeOrders',
  range_tickets: 'rangeTickets',
  range_revenue_minor: 'rangeRevenueMinor',
  range_refunds_minor: 'rangeRefundsMinor',
  customer_type: 'customerType',
  first_touch: 'firstTouch',
  is_member: 'isMember',
  is_vip: 'isVip',
  consent: 'consent',
  segments: 'segments',
} as const satisfies Record<keyof CustomerRowOut, CustomerField>

function isReason(value: string): value is CustomerUnavailableReason {
  return (CUSTOMER_UNAVAILABLE_REASONS as readonly string[]).includes(value)
}

function isWireField(key: string): key is keyof CustomerRowOut {
  return Object.hasOwn(FIELDS, key)
}

export function customerRowFromWire(row: CustomerRowOut): CustomerRow {
  return {
    customerId: row.customer_id,
    customerRef: row.customer_ref,
    websiteId: row.website_id,
    environment: row.environment,
    synced: row.synced,
    registeredAt: row.registered_at,
    firstPurchaseAt: row.first_purchase_at,
    lastPurchaseAt: row.last_purchase_at,
    orders: row.orders,
    tickets: row.tickets,
    lifetimeRevenueMinor: row.lifetime_revenue_minor,
    refundsMinor: row.refunds_minor,
    aovMinor: row.aov_minor,
    rangeOrders: row.range_orders,
    rangeTickets: row.range_tickets,
    rangeRevenueMinor: row.range_revenue_minor,
    rangeRefundsMinor: row.range_refunds_minor,
    customerType: row.customer_type,
    firstTouch: row.first_touch && {
      touchedAt: row.first_touch.touched_at,
      source: row.first_touch.source,
      medium: row.first_touch.medium,
      campaign: row.first_touch.campaign,
      channelId: row.first_touch.channel_id,
    },
    isMember: row.is_member,
    isVip: row.is_vip,
    consent: row.consent && {
      marketingState: row.consent.marketing_state,
      analytics: row.consent.analytics,
      advertising: row.consent.advertising,
      email: row.consent.email,
      push: row.consent.push,
      updatedAt: row.consent.updated_at,
    },
    segments: null,
  }
}

/**
 * `not_available` keyed by row field. A column or reason this client does not know (one
 * a later API adds) is left out rather than guessed at: its `null`s then read "—".
 */
function notAvailableFromWire(body: Record<string, string>): CustomersList['notAvailable'] {
  const out: CustomersList['notAvailable'] = {}
  for (const [key, reason] of Object.entries(body)) {
    if (isWireField(key) && isReason(reason)) out[FIELDS[key]] = reason
  }
  return out
}

export function customersFromWire(body: CustomersOut): CustomersList {
  const { data } = body
  return {
    rows: data.rows.map(customerRowFromWire),
    total: data.total,
    basis: data.basis,
    sort: data.sort,
    order: data.order,
    limit: data.limit,
    offset: data.offset,
    notAvailable: notAvailableFromWire(data.not_available),
    currency: body.meta.currency,
    timezone: body.meta.timezone,
  }
}

export function customerRevealFromWire(body: CustomerRevealOut): CustomerReveal {
  return {
    customerId: body.customer_id,
    websiteId: body.website_id,
    environment: body.environment,
    externalCustomerId: body.external_customer_id,
  }
}


/**
 * The query string every list route reads: the Overview's scope (`from`/`to`,
 * `website_id`, the environments), then who is listed, the filters, the sort and, for
 * the JSON routes, the page. Never a search term: that travels in the search's body.
 */
export function customersSearch(query: CustomersQuery, now: Date): string {
  const params = new URLSearchParams(overviewSearch(query.filters, now))
  if (query.basis) params.set('basis', query.basis)
  if (query.customerType) params.set('customer_type', query.customerType)
  if (query.member !== undefined) params.set('member', String(query.member))
  if (query.vip !== undefined) params.set('vip', String(query.vip))
  for (const state of query.consent ?? []) params.append('consent', state)
  if (query.sort) params.set('sort', query.sort)
  if (query.order) params.set('order', query.order)
  if (query.limit !== undefined) params.set('limit', String(query.limit))
  if (query.offset !== undefined) params.set('offset', String(query.offset))
  return params.toString()
}

/** The search's JSON body: exactly one of the two keys the API takes. */
export function customerSearchBody(search: CustomerSearch): { email: string } | { external_customer_id: string } {
  return 'email' in search ? { email: search.email } : { external_customer_id: search.externalCustomerId }
}

/** The name the API gives the file (`customers-<from>-to-<to>.csv`), for an answer that names none. */
export function customersCsvFilename(from: string, to: string): string {
  return csvFilename('customers', from, to)
}
